/**
 * The findings validation holds on one element, as the instrument editor and the line form
 * show them: one row per finding with its severity, its message and its rule, and a link per
 * related element that highlights that element on the drawing. While the form holds a draft,
 * they are the draft's findings. While a draft is being validated (`isValidating`) the rows read
 * as stale: dimmed, with a spinner in place of each row's mark. An element with no finding
 * renders nothing.
 */
import { useLayoutEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { CircleX } from 'lucide-react';
import type { Finding, Severity } from '../types';
import { findingAnchorId } from './findingFields';
import { relatedElementLabel, type ChangedElementFinding, type FindingRelatedElements } from './findingRows';
import { AppIcon } from './LucideIcon';
import './validation-findings.css';

/** A BLOCKER has a computable answer; a FLAG waits on a person, so it reads as a warning. */
const SEVERITY_TONE: Record<Severity, 'error' | 'warning'> = {
  BLOCKER: 'error',
  FLAG: 'warning',
  ADVISORY: 'warning',
};

function findingsListClass(isValidating: boolean): string {
  return isValidating ? 'pnid-findings pnid-findings--stale' : 'pnid-findings';
}

export default function ValidationFindings({ findings, elements, onHighlightEntity, isValidating = false }: {
  findings: Finding[];
  elements: FindingRelatedElements;
  onHighlightEntity?: (id: string | null) => void;
  isValidating?: boolean;
}) {
  if (!findings.length) return null;
  return (
    <ul className={findingsListClass(isValidating)} aria-label="Findings" aria-busy={isValidating}>
      {findings.map((finding, index) => {
        const related = finding.related_ids
          .map((id) => ({ id, label: relatedElementLabel(id, elements) }))
          .filter((entry): entry is { id: string; label: string } => entry.label !== null);
        return (
          <li key={`${finding.rule}-${index}`} className="pnid-finding">
            <span className="pnid-finding-mark">
              {isValidating
                ? <CircularProgress size={14} color="inherit" aria-label="Checking findings" />
                : <AppIcon icon={CircleX} size={14} />}
            </span>
            <div className="pnid-finding-body">
              <div className="pnid-finding-head">
                <span className={`pnid-finding-severity pnid-finding-severity--${SEVERITY_TONE[finding.severity]}`}>
                  {finding.severity}
                </span>
                <span className="pnid-finding-message">{finding.message}</span>
                <span className="pnid-finding-rule">{finding.rule}</span>
              </div>
              {related.length > 0 && (
                <div className="pnid-finding-related">
                  {related.map(({ id, label }) => (
                    <button key={id} type="button" className="pnid-finding-link"
                      title="Highlight on the drawing"
                      onClick={() => onHighlightEntity?.(id)}
                      onMouseEnter={() => onHighlightEntity?.(id)}
                      onMouseLeave={() => onHighlightEntity?.(null)}
                      onFocus={() => onHighlightEntity?.(id)}
                      onBlur={() => onHighlightEntity?.(null)}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';

/** Brings a field's container into view and focuses its first control. A read-only form has
 *  no enabled control, so it only scrolls. */
function goToField(anchor: HTMLElement) {
  // jsdom has no scrollIntoView, and the scroll is presentation only.
  if (typeof anchor.scrollIntoView === 'function') anchor.scrollIntoView({ block: 'center' });
  anchor.querySelector<HTMLElement>(FOCUSABLE)?.focus();
}

/**
 * Every finding on one element, at the end of its form: severity, message and rule. A row whose
 * field is on the form is a link to it. The form gives each field container the id
 * `findingAnchorId(formId, field)`; a field the form does not show (Attach To in Branch/Join
 * mode, for one) leaves its rows as plain text. While the form holds a draft, the list ends with
 * the current findings of each other element the draft changes, each named by its element.
 * While a draft is being validated (`isValidating`) the rows are dimmed and the heading carries
 * a spinner.
 */
export function FindingsList({ findings, changedElements = [], formId, isValidating = false }: {
  findings: Finding[];
  changedElements?: ChangedElementFinding[];
  formId: string;
  isValidating?: boolean;
}) {
  // Which fields are on the form is read from the document after each render: the form decides
  // it through its mode, its layout and its schema, and the list sits outside all three.
  const [shownFields, setShownFields] = useState<string>('');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every render on purpose; the setter bails out on an equal value
  useLayoutEffect(() => {
    const present = [...new Set(findings.map((finding) => finding.field))]
      .filter((field) => document.getElementById(findingAnchorId(formId, field)))
      .join(' ');
    setShownFields((previous) => (previous === present ? previous : present));
  });
  const rowCount = findings.length + changedElements.length;
  if (!rowCount) return null;
  const headingId = `${formId}-findings-heading`;
  return (
    <section className="pnid-findings-summary" aria-labelledby={headingId}>
      <h3 id={headingId} className="pnid-findings-summary-title">
        Findings ({rowCount})
        {isValidating && <CircularProgress size={14} color="inherit" aria-label="Checking findings" />}
      </h3>
      <ul className={findingsListClass(isValidating)} aria-labelledby={headingId} aria-busy={isValidating}>
        {findings.map((finding, index) => {
          const { field } = finding;
          const content = (
            <>
              <span className="pnid-finding-mark"><AppIcon icon={CircleX} size={14} /></span>
              <span className="pnid-finding-head">
                <span className={`pnid-finding-severity pnid-finding-severity--${SEVERITY_TONE[finding.severity]}`}>
                  {finding.severity}
                </span>
                <span className="pnid-finding-message">{finding.message}</span>
                <span className="pnid-finding-rule">{finding.rule}</span>
              </span>
            </>
          );
          return (
            <li key={`${finding.rule}-${index}`} className="pnid-finding pnid-finding--summary">
              {shownFields.split(' ').includes(field) ? (
                <button type="button" className="pnid-finding-goto"
                  onClick={() => {
                    const anchor = document.getElementById(findingAnchorId(formId, field));
                    if (anchor) goToField(anchor);
                  }}>
                  {content}
                </button>
              ) : <span className="pnid-finding-static">{content}</span>}
            </li>
          );
        })}
        {changedElements.map(({ key, finding, text }) => (
          <li key={key} className="pnid-finding pnid-finding--summary">
            <span className="pnid-finding-static">
              <span className="pnid-finding-mark"><AppIcon icon={CircleX} size={14} /></span>
              <span className="pnid-finding-head">
                <span className={`pnid-finding-severity pnid-finding-severity--${SEVERITY_TONE[finding.severity]}`}>
                  {finding.severity}
                </span>
                <span className="pnid-finding-message">{text}</span>
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
